<template>
  <div class="consumer-gift-card-payment-success">
    <div class="header">
      <h2>{{ $t('components.consumer_gift_card_payment_success.title') }}</h2>

      <div class="h-text-2 pt-3" v-html="$t('components.consumer_gift_card_payment_success.cta_my_account')"></div>

      <h-link class="download-receipt" outlined large :loading="receiptWorking" v-show="orderIsCompleted" @click="downloadReceipt">
        {{ $t('components.consumer_gift_card_payment_success.download_receipt') }} &nbsp; <v-icon size="20" center class="mr-2">mdi-open-in-new</v-icon>
      </h-link>

      <div v-if="!orderIsCompleted" class="h-text-2 pending-order-information" v-html="$t('components.consumer_gift_card_payment_success.cta_my_account_pending_order')"></div>
    </div>

    <div class="content">
      <div v-if="!redeemCompleted">
        <label class="h-label-2">{{ $t('components.consumer_gift_card_payment_success.what_to_do_title') }}</label>

        <v-radio-group class="buy-for-radio-group" hide-details v-model="buyForGift" mandatory>
          <v-radio class="h-label-2" :label="$t('components.consumer_gift_card_payment_success.for_someone_else_label')" :value="true" />
          <v-radio class="h-label-2" :label="$t('components.consumer_gift_card_payment_success.for_yourself_label')" :value="false" />
        </v-radio-group>

        <div class="box text-center" v-if="getRedeemContextWorking">
          <v-progress-circular :size="100" :width="7" color="primary" indeterminate />
        </div>

        <div v-else-if="buyForGift" class="box">
          <h-flow-info :info-steps="infoSteps">
            <template v-slot:step-1-subtext>
              <h-action-layout style="flex-direction: column; gap: var(--gap-xsmall)" v-if="orderIsCompleted">
                <consumer-gift-card-download-pdf-modal ref="downloadPdfModal" :voucher-id="voucherId" :activation-code-id="activationCodeId"></consumer-gift-card-download-pdf-modal>
                <h-btn color="black" medium :loading="downloadPdfModalOpening" @click="openDownloadPdfModal" style="flex-basis: auto"
                  ><v-icon left>mdi-wallet-giftcard</v-icon>{{ $t('components.consumer_gift_card_download_pdf_modal.action_title') }}
                </h-btn>
                <consumer-gift-card-send-by-email-modal ref="sendByEmailModal" :voucher-id="voucherId" :activation-code-id="activationCodeId"> </consumer-gift-card-send-by-email-modal>
                <h-btn medium :loading="sendByEmailModalOpening" @click="openSendByEmailModal" style="flex-basis: auto"><v-icon left>mdi-email-outline</v-icon>{{ $t('components.consumer_gift_card_send_by_email_modal.action_title') }}</h-btn>
              </h-action-layout>
            </template>
          </h-flow-info>
        </div>

        <div v-else-if="!buyForGift" class="box">
          <div v-if="!hasLimitDayError && !redeemCompleted">
            <div v-if="currentRedeemAction === 'redeem'">
              <label>{{ $t('components.consumer_gift_card_payment_success.redeem_label') }}</label>
              <card-selection context="activation" :has-application="accountHasApplication && !hasClassicCard" class="card-selection" :card-list="currentRedeemInformation.cards" @change="(card) => setCurrentCard(card)" />
              <h-btn min-width="352" :loading="redeemWorking" @click="redeem"> <v-icon size="20" center class="mr-2">mdi-credit-card-plus-outline</v-icon> {{ $t('components.consumer_gift_card_payment_success.load_card_submit') }}</h-btn>
              <p class="h-text-4 pt-6">{{ $t('components.consumer_gift_card_payment_success.lost_card_info') }}</p>
            </div>

            <div v-else-if="currentRedeemAction === 'request_card'">
              <h-flow-info class="no-card-info" :info-steps="noCardInfoSteps">
                <template v-slot:step-1-subtext>
                  <h-btn @click="showCardTypeSelection" :loading="redeemWorking || getRedeemContextWorking" class="mt-6" large style="width: 100%">
                    <v-icon left>mdi-credit-card-outline</v-icon>{{ $t('components.consumer_gift_card_payment_success.no_card.step_1.get_my_card') }}
                  </h-btn>
                </template>
              </h-flow-info>
            </div>

            <div v-else-if="currentRedeemAction === 'renew_card'">
              <p v-if="renewableCard.card_program_type === 'instant'">{{ $t('components.consumer_gift_card_payment_success.renew_card.renew_instant_message', { publicToken: $options.filters.publicToken(renewableCard.public_token) }) }}</p>
              <p v-else>{{ $t('components.consumer_gift_card_payment_success.renew_card.renew_classic_message', { publicToken: $options.filters.publicToken(renewableCard.public_token) }) }}</p>

              <overlay-renew-card-modal :public-token="renewableCard.public_token" :card-program-type="renewableCard.card_program_type" @success="renewCardSuccess">
                <template v-slot:activator="{ on, attrs }">
                  <h-btn class="renew-btn" :loading="redeemWorking" v-on="on" v-bind="attrs" large><v-icon left>mdi-credit-card-outline</v-icon>{{ $t('components.consumer_gift_card_payment_success.renew_card.renew_btn') }}</h-btn>
                </template>
              </overlay-renew-card-modal>
            </div>
          </div>

          <div v-else-if="hasLimitDayError && !redeemCompleted">
            <h2 class="text-error">{{ $t('components.consumer_gift_card_payment_success.error_redeem_limit.title') }}</h2>

            <p
              v-if="dailyRedeemLimitError && dailyRedeemLimitError.code === 'errors.http.reached_account_redeem_limit_day'"
              v-html="translateError(this, 'components.consumer_gift_card_payment_success.error_redeem_limit.security_reason', dailyRedeemLimitError)"
            ></p>
            <p
              v-if="dailyRedeemLimitError && dailyRedeemLimitError.code === 'errors.http.reached_voucher_redeem_limit_day'"
              v-html="
                translateError(this, 'components.consumer_gift_card_payment_success.error_redeem_limit.security_reason_voucher', dailyRedeemLimitError, {
                  nbCardText: $tc('components.consumer_gift_card_payment_success.error_redeem_limit.nb_card', dailyRedeemLimitError.data.limit, { limit: dailyRedeemLimitError.data.limit }),
                })
              "
            ></p>

            <p v-if="dailyRedeemLimitError && dailyRedeemLimitError.code === 'errors.http.reached_account_redeem_limit_day'" v-html="$t('components.consumer_gift_card_payment_success.error_redeem_limit.download_my_account')"></p>
            <p v-if="dailyRedeemLimitError && dailyRedeemLimitError.code === 'errors.http.reached_voucher_redeem_limit_day'" v-html="$t('components.consumer_gift_card_payment_success.error_redeem_limit.download_my_account_voucher')"></p>

            <h-btn @click="download" large style="flex-basis: auto"><v-icon left>mdi-wallet-giftcard</v-icon>{{ $t('components.consumer_gift_card_payment_success.error_redeem_limit.download') }}</h-btn>
            <p class="h-text-4" style="margin-top: var(--gap)">{{ $t('components.consumer_gift_card_payment_success.error_redeem_limit.lost_card') }}</p>
          </div>
        </div>
      </div>

      <div v-if="!hasLimitDayError && redeemCompleted">
        <p v-if="fromRenewCard">
          {{ $t('components.consumer_gift_card_payment_success.redeem_completed_from_renew_message', { publicToken: $options.filters.publicToken(currentCardPublicToken) }) }}
        </p>
        <p v-else-if="fromCardApplication">
          {{ $t('components.consumer_gift_card_payment_success.redeem_completed_from_card_application_message', { publicToken: $options.filters.publicToken(currentCardPublicToken) }) }}
        </p>
        <p v-else>
          {{ $t('components.consumer_gift_card_payment_success.redeem_completed_message', { publicToken: $options.filters.publicToken(currentCardPublicToken) }) }}
        </p>

        <voucher-amount class="summary" :class="{ 'contribution-only': contributionOnly }" :voucher="currentRedeemResult" />
      </div>

      <div class="link-section">
        <merchants-modal class="link-merchants" :offer_id="offer.id">
          <template #activator="{ on, attrs }">
            <h-link class="link-merchants" large v-bind="attrs" v-on="on">{{ $t('components.consumer_gift_card_payment_success.cta_merchants') }}</h-link>
          </template>
        </merchants-modal>
        <h-link v-if="voucherReady" class="link-account" large :to="{ name: 'my-account' }" v-html="$t('components.consumer_gift_card_payment_success.cta_my_account_2')"></h-link>
        <h-link v-if="voucherReady" class="link-cancel" large @click="closeOverlay">{{ $t('components.consumer_gift_card_payment_success.cta_cancel') }}</h-link>
      </div>

      <overlay-app class="mt-6" v-if="redeemCompleted" />

      <div v-if="!voucherReady" class="voucher-not-ready-container">
        <v-progress-circular v-if="!pollTimeExceeded && !inError" :size="100" :width="7" indeterminate color="primary" />
        <h-btn v-else :to="{ name: 'my-account' }">{{ $t('components.consumer_gift_card_payment_success.redirect_consumer_gifts') }}</h-btn>
      </div>
    </div>

    <card-type-selection-modal
      v-model="showCardTypeSelectionModal"
      @success="cardApplicationSuccess"
      :redirect-link="classicCardRedirectLink"
      :before-classic-card-apply-function="beforeClassicCardApplyFunction"
      :display-success-in-card-type-selection-modal="false"
    />
  </div>
</template>

<script>
import safeExecute from '@/composables/safe-execute';

import { mapActions, mapGetters } from 'vuex';
import MerchantsModal from '@/components/modals/MerchantsModal';
import ConsumerGiftCardSendByEmailModal from '../../consumer-gift-card/components/ConsumerGiftCardSendByEmailModal.vue';
import ConsumerGiftCardDownloadPdfModal from '@/components/consumer-gift-card/components/ConsumerGiftCardDownloadPdfModal.vue';

import CardSelection from '@/components/pay-and-get/CardSelection.vue';
import { recaptcha } from '@/mixins';
import OverlayApp from '@/components/overlays/components/OverlayApp.vue';
import CardTypeSelectionModal from '@/components/account/components/CardTypeSelectionModal.vue';
import VoucherAmount from '@/components/vouchers/VoucherAmount.vue';

import OverlayRenewCardModal from '@/components/overlays/components/OverlayRenewCardModal.vue';
import errorHandling from '@/composables/error-handling';
import _get from 'lodash/get';

export default {
  name: 'purchase-consumer-gift-card-success',
  props: {
    offer: {
      type: Object,
    },
  },
  mixins: [recaptcha],
  components: {
    VoucherAmount,
    CardTypeSelectionModal,
    OverlayApp,
    OverlayRenewCardModal,
    CardSelection,
    ConsumerGiftCardDownloadPdfModal,
    MerchantsModal,
    ConsumerGiftCardSendByEmailModal,
  },
  async created() {
    if (this.canPoll) {
      this.doPoll = true;
    }
  },
  setup(props, { root }) {
    const { executeOverlay } = safeExecute(root.$store);
    const { translateError } = errorHandling(root.$store, root.$options.filters);
    return {
      executeOverlay,
      translateError,
    };
  },
  mounted() {
    this.getRedeemContext();
  },
  data() {
    return {
      sendByEmailModalOpening: false,
      downloadPdfModalOpening: false,

      showCardTypeSelectionModal: false,

      buyForGift: true,

      currentRedeemAction: null,

      hasLimitDayError: false,
      dailyRedeemLimitError: null,

      fromRenewCard: false,
      fromCardApplication: false,

      redeemCompleted: false,
      redeemWorking: false,

      classicCardRedirectLink: {
        url: `${window.location.origin}/my-account`,
        label_fr: this.$t('components.card_application.redirect_link.return_to_account_label', 'fr'),
        label_en: this.$t('components.card_application.redirect_link.return_to_account_label', 'en'),
      },

      getRedeemContextWorking: false,
      giftCardDownloadWorking: false,
      receiptWorking: false,
      sleepTimeMs: 5 * 1000,
      maxPollTimeMs: 5 * 60 * 1000,
      pollTimeMs: 0,
      isPolling: false,
      doPoll: false,
      timeoutId: null,
      inError: false,
    };
  },
  beforeDestroy() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  },
  watch: {
    canPoll() {
      if (this.canPoll) {
        this.doPoll = true;
      }
    },
    doPoll() {
      if (this.doPoll) {
        this.startPolling();
      }
    },
  },
  computed: {
    ...mapGetters('ui', ['currentLocale']),
    ...mapGetters('consumerGiftCard', ['voucherId', 'consumerGiftCardOrderId', 'orderIsCompleted', 'activationCodeId']),

    ...mapGetters('account', ['accountHasApplication', 'hasClassicCard', 'accountActiveMemberships']),
    ...mapGetters('overlay', ['headerDrawerContentPayload']),
    ...mapGetters('overlayRedeem', ['currentCardholderId', 'currentCardPublicToken', 'currentRedeemInformation', 'currentRedeemResult', 'renewableCard']),

    ...mapGetters('security', ['authenticated']),

    ...mapGetters('error', ['isTargetErrorGetter']),

    contributionOnly() {
      return this.currentRedeemResult.bonus_amount === 0;
    },
    canPoll() {
      return this.consumerGiftCardOrderId !== null;
    },
    pollTimeExceeded() {
      return this.pollTimeMs > this.maxPollTimeMs;
    },
    shouldAutoPoll() {
      return this.canPoll && !this.voucherReady && !this.pollTimeExceeded;
    },
    voucherReady() {
      return this.orderIsCompleted && this.activationCodeId && this.voucherId;
    },
    noCardInfoSteps() {
      return [
        {
          step: 1,
          src: 'card_envelop.svg',
          alt: this.$t('components.consumer_gift_card_payment_success.no_card.step_1.image_alt_text'),
          info_text: this.$t('components.consumer_gift_card_payment_success.no_card.step_1.info_text'),
        },
        {
          step: 2,
          src: 'pos.svg',
          alt: this.$t('components.consumer_gift_card_payment_success.no_card.step_2.image_alt_text'),
          info_text: this.$t('components.consumer_gift_card_payment_success.no_card.step_2.info_text'),
        },
      ];
    },
    infoSteps() {
      return [
        {
          step: 1,
          src: 'printed_voucher.svg',
          alt: this.$t('components.activation_flow_info.step_1.image_alt_text'),
          info_text: this.$t('components.consumer_gift_card_payment_success.step_1.info_text'),
        },
        {
          step: 2,
          src: 'card_envelop.svg',
          alt: this.$t('components.activation_flow_info.step_2.image_alt_text'),
          info_text: this.$t('components.consumer_gift_card_payment_success.step_2.info_text'),
          info_subtext: this.paymentNotice,
        },
      ];
    },
    paymentNotice() {
      let notice = '';

      if (_get(this.offer, `features.transfer_notice.${this.currentLocale}`)) {
        notice += this.offer.features.transfer_notice[this.currentLocale] + `\n`;
        notice += this.$t('components.consumer_gift_card_payment_success.step_2.info_subtext');
      }
      return notice;
    },
  },
  methods: {
    ...mapActions('overlay', ['closeHeaderDrawer']),
    ...mapActions('overlayRedeem', ['setCurrentCard']),
    ...mapActions('error', ['useTargetError', 'clearError']),

    async beforeClassicCardApplyFunction() {
      return await this.redeem();
    },
    async openSendByEmailModal() {
      this.sendByEmailModalOpening = true;
      try {
        await this.$refs.sendByEmailModal.openModal();
      } finally {
        this.sendByEmailModalOpening = false;
      }
    },
    async openDownloadPdfModal() {
      this.downloadPdfModalOpening = true;
      try {
        await this.$refs.downloadPdfModal.openModal();
      } finally {
        this.downloadPdfModalOpening = false;
      }
    },
    async cardApplicationSuccess() {
      this.showCardTypeSelectionModal = false;

      await this.getRedeemContext();
      await this.redeem();
    },
    async renewCardSuccess() {
      this.fromRenewCard = true;

      await this.redeem();
    },
    showCardTypeSelection() {
      this.fromCardApplication = true;

      this.executeOverlay({
        action: 'account/getSelfGiftingAcquiringCardContext',
        success: () => {
          this.showCardTypeSelectionModal = true;
        },
      });
    },
    async getRedeemContext() {
      this.getRedeemContextWorking = true;

      await this.executeOverlay(
        {
          action: 'overlayRedeem/getRedeemDetailForOverlay',
          success: (redeemContext) => {
            // NOTE (MAA) Only track the initial action from get redeem.
            if (!this.currentRedeemAction) {
              this.currentRedeemAction = redeemContext.action;
            }
          },
          beforeError: (err) => {
            if (err.code === 'errors.http.reached_account_redeem_limit_day') {
              this.hasLimitDayError = true;
              this.dailyRedeemLimitError = err;
              this.useTargetError('use_account_redeem_limit_day');
            }
            if (err.code === 'errors.http.reached_voucher_redeem_limit_day') {
              this.hasLimitDayError = true;
              this.dailyRedeemLimitError = err;
              this.useTargetError('use_account_redeem_limit_day');
            }
          },
          finally: () => {
            this.getRedeemContextWorking = false;
          },
        },
        { ...this.headerDrawerContentPayload, use_activation_rules: true, purchase_channel: 'consumer_interface' },
      );
    },
    async redeem() {
      this.redeemWorking = true;

      return await this.executeOverlay(
        {
          action: 'overlayRedeem/selfRedeem',
          beforeError: (err) => {
            if (err.code === 'errors.http.exceed.offer.publisher.inventory.limit' || err.code === 'errors.http.exceed.offer.account.inventory.limit') {
              err.data = {
                offer_branding_label: this.$options.filters.translateI18nObject(this.offer, 'offer_branding.label'),
                limit_amount: this.$options.filters.currencySymbol(this.$options.filters.currency(this.offer.incentive.user_inventory_limit)),
                remaining_amount: this.$options.filters.currencySymbol(this.$options.filters.currency(this.currentRedeemInformation.user_payment_remaining)),
              };
            }
          },
          success: () => {
            this.redeemCompleted = true;

            if (window.fbq) {
              fbq('trackCustom', 'ClaimVoucher', {
                partner_id: this.headerDrawerContentPayload.partner_id,
                offer_id: this.offer.id,
                category_id: this.offer.category_id,
                voucher: this.offerIncentiveBonusAmount,
              });
            }
          },
          finally: () => {
            this.redeemWorking = false;
          },
        },
        { activation_code_id: this.activationCodeId, recaptcha_token: await this.recaptcha('giftCertificateSelfRedeem'), cardholder_id: this.currentCardholderId },
      );
    },
    async download() {
      await this.executeOverlay(
        {
          action: 'consumerGiftCard/downloadGiftCard',
        },
        {
          voucher_id: this.voucherId,
          lang: this.currentLocale,
          filename: this.$t('components.consumer_gift_card_download_pdf_modal.filename', this.currentLocale, {
            activation_code_id: this.activationCodeId,
          }),
        },
      );
    },
    closeOverlay() {
      this.closeHeaderDrawer();
    },
    async startPolling() {
      this.isPolling = true;
      this.doAutoPoll();
    },
    async doAutoPoll() {
      if (this.canPoll) {
        this.getConsumerGiftCardOrderStatus(this.autoPollCallback);
        this.pollTimeMs += this.sleepTimeMs;
      }
    },
    async autoPollCallback() {
      if (this.shouldAutoPoll && !this.inError) {
        this.timeoutId = setTimeout(() => {
          this.doAutoPoll();
          this.timeoutId = null;
        }, this.sleepTimeMs);
      } else {
        this.isPolling = false;
      }
    },
    async doManualPoll() {
      this.getConsumerGiftCardOrderStatus();
    },
    async getConsumerGiftCardOrderStatus(successCallback) {
      if (this.canPoll) {
        return this.executeOverlay(
          {
            action: 'consumerGiftCard/getOrderDetails',
            success: () => {
              if (successCallback) {
                successCallback();
              }
            },
            afterError: () => {
              if (this.timeoutId) {
                clearTimeout(this.timeoutId);
              }
              this.inError = true;
            },
          },
          this.consumerGiftCardOrderId,
        );
      }
    },
    async downloadReceipt() {
      this.receiptWorking = true;

      await this.executeOverlay(
        {
          action: 'consumerGiftCard/downloadReceipt',
          finally: () => {
            this.receiptWorking = false;
          },
        },
        {
          consumer_gift_card_order_id: this.consumerGiftCardOrderId,
          language: this.currentLocale,
        },
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.no-card-info ::v-deep .item-icon {
  width: 100px;
}

.renew-btn {
  width: 100%;
  max-width: 352px;
}

.consumer-gift-card-payment-success {
  border: var(--border-thin) solid var(--color-border);
}

.buy-for-radio-group {
  margin: var(--gap) 0;
  padding-top: 0;

  ::v-deep .v-label {
    color: var(--color-text-primary);
  }
}

.card-selection {
  ::v-deep .v-label {
    color: var(--color-text-primary);
  }
}

.header {
  background-color: #f2f2f2;

  padding: var(--gap);
}

.text-error {
  color: var(--color-error);
  margin-bottom: var(--gap);
}

.content {
  padding: var(--gap);
}

.box {
  border: 1px solid var(--color-border);
  padding: var(--gap);
}

.pending-order-information {
  margin-top: var(--gap);
}

.download-receipt {
  text-transform: revert;

  margin-top: var(--gap);
}

.v-btn.download-receipt {
  padding-left: 0px;
  border: none;
}

.link-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--gap-small);

  padding-top: var(--gap);
}

.voucher-not-ready-container {
  text-align: center;
  margin: var(--gap);
}
</style>
